import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconPickerContent from './IconPickerContent';
import { positionMenuBesideAnchor } from '../../../DocEditor/utils/floatingMenuPosition';

/**
 * Portaled icon/emoji picker.
 *
 * - `anchorRect`: same beside-anchor placement as the doc drag-handle menu
 *   (prefer left, stay on screen, keep vertical contact with the anchor)
 * - `position`: legacy top-left point (doc title / other chrome) with viewport clamp
 */
const IconPicker = ({
  currentIcon,
  onIconChange,
  onIconDeleted,
  onClose,
  position,
  anchorRect,
  nodeColor,
}) => {
  const wrapRef = useRef(null);
  const [besidePos, setBesidePos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorRect) {
      setBesidePos(null);
      return undefined;
    }
    const el = wrapRef.current;
    if (!el) return undefined;

    const place = () => {
      setBesidePos(
        positionMenuBesideAnchor(el, anchorRect, {
          fallbackW: 360,
          fallbackH: 440,
        })
      );
    };

    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRect]);

  if (typeof document === 'undefined') return null;

  let left;
  let top;
  let ready = true;

  if (anchorRect) {
    left = besidePos ? besidePos.x : 0;
    top = besidePos ? besidePos.y : 0;
    ready = !!besidePos;
  } else {
    // Adjust position to stay in viewport
    left = position?.x ?? 100;
    top = position?.y ?? 100;
    const w = 380;
    const h = 440;
    const p = 16;

    if (left + w > window.innerWidth - p) left = window.innerWidth - w - p;
    if (left < p) left = p;
    if (top + h > window.innerHeight - p) top = window.innerHeight - h - p;
    if (top < p) top = p;
  }

  return createPortal(
    <div
      ref={wrapRef}
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 10000,
        visibility: ready ? 'visible' : 'hidden',
      }}
    >
      <IconPickerContent
        currentIcon={currentIcon}
        onIconChange={onIconChange}
        onIconDeleted={onIconDeleted}
        onClose={onClose}
        isVisible={true}
        nodeColor={nodeColor}
      />
    </div>,
    document.body
  );
};

export default IconPicker;
